import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, RefreshCw, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isAdminEmail } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  adminListAssessments,
  adminGetAssessment,
  adminUpdateExpertReview,
  adminRegenerateReport,
} from "@/lib/visa-readiness/admin.functions";

export const Route = createFileRoute("/admin/visa-assessments")({
  head: () => ({ meta: [{ title: "Visa Assessments - Admin" }, { name: "robots", content: "noindex" }] }),
  component: AdminVisaAssessments,
});

type Row = {
  id: string;
  user_id: string;
  status: string;
  current_step: number;
  created_at: string;
  updated_at: string | null;
};

function AdminVisaAssessments() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);

  const listFn = useServerFn(adminListAssessments);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      const email = data.user?.email ?? null;
      if (!email || !isAdminEmail(email)) {
        navigate({ to: "/" });
        return;
      }
      setReady(true);
      try {
        const list = await listFn();
        setRows(list as Row[]);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [listFn, navigate]);

  if (!ready) return null;

  return (
    <AdminShell title="Visa Assessments" description="AI-generated readiness reports and expert-review queue.">
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="rounded-2xl border bg-card">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Step</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-mono text-xs">{r.user_id.slice(0, 8)}…</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${r.status === "completed" ? "bg-primary/10 text-primary" : "bg-muted"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2">{r.current_step}/8</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setSelected(r.id)}>
                      <Eye className="mr-1 h-3.5 w-3.5" /> Open
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-muted-foreground">No assessments yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {selected && <DetailDialog id={selected} onClose={() => setSelected(null)} />}
    </AdminShell>
  );
}

function DetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof fetchDetail>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<"pending" | "in_review" | "completed">("pending");
  const [busy, setBusy] = useState(false);

  const fetchDetail = useServerFn(adminGetAssessment);
  const updateReview = useServerFn(adminUpdateExpertReview);
  const regen = useServerFn(adminRegenerateReport);

  useEffect(() => {
    (async () => {
      try {
        const d = await fetchDetail({ data: { id } });
        setDetail(d);
        if (d.review) {
          setNotes(((d.review as { admin_notes?: string }).admin_notes) ?? "");
          setStatus(((d.review as { status?: "pending" | "in_review" | "completed" }).status) ?? "pending");
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, fetchDetail]);

  const handleSave = async () => {
    if (!detail?.review) return;
    setBusy(true);
    try {
      await updateReview({ data: { reviewId: (detail.review as { id: string }).id, notes, status } });
      toast.success("Review saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRegen = async () => {
    if (!detail?.assessment) return;
    setBusy(true);
    try {
      await regen({ data: { assessmentId: (detail.assessment as { id: string }).id } });
      toast.success("Regenerated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Regenerate failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Assessment detail</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : !detail ? (
          <p className="text-sm">Not found.</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/30 p-3 text-xs">
              <pre className="whitespace-pre-wrap break-words">{JSON.stringify((detail.assessment as { form_data?: unknown })?.form_data ?? {}, null, 2).slice(0, 2000)}</pre>
            </div>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Reports ({detail.reports.length})</p>
              <ul className="mt-2 space-y-1 text-sm">
                {detail.reports.map((r) => (
                  <li key={(r as { id: string }).id} className="flex justify-between rounded border p-2">
                    <span>{new Date((r as { created_at: string }).created_at).toLocaleString()}</span>
                    <span className="font-semibold">{((r as { scores?: { overall?: number } }).scores?.overall) ?? "-"}/100</span>
                  </li>
                ))}
              </ul>
              <Button size="sm" variant="outline" className="mt-2" onClick={handleRegen} disabled={busy}>
                <RefreshCw className="mr-1 h-3.5 w-3.5" /> Regenerate report
              </Button>
            </div>

            {detail.review && (
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Expert review</p>
                <Select value={status} onValueChange={(v) => setStatus(v as "pending" | "in_review" | "completed")}>
                  <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_review">In review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea className="mt-2" rows={5} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={5000} placeholder="Specialist notes for the student…" />
                <Button size="sm" className="mt-2" onClick={handleSave} disabled={busy}>Save review</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
